import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";
import {
  BOARD_CONFIG,
  BOARD_DISPLAY_NAME,
  IS_MIX_CATALOG,
} from "@/config/browserBoard";
import { AnnounceBanner } from "@/features/announce/components/AnnounceBanner";
import { DesktopAnnouncementContent } from "@/features/announce/components/DesktopAnnouncementContent";
import { CatalogGrid } from "@/features/catalog/components/lists";
import { CatalogSortModal } from "@/features/catalog/components/modals/CatalogSortModal";
import { TagFilterModal } from "@/features/catalog/components/modals/TagFilterModal";
import { CatalogLoadMore } from "@/features/catalog/components/navigation/CatalogLoadMore";
import { CatalogPagination } from "@/features/catalog/components/navigation/CatalogPagination";
import { DesktopCatalogToolbar } from "@/features/catalog/components/navigation/DesktopCatalogToolbar";
import { MobileCatalogFooter } from "@/features/catalog/components/navigation/MobileCatalogFooter";
import { useInfiniteThreads, useThreads } from "@/features/catalog/hooks";
import {
  type CatalogEvent,
  type CatalogEventNotifier,
  isCatalogEvent,
} from "@/features/catalog/mediators/catalogEvents";
import {
  type CatalogPageEffect,
  createCatalogPageMediator,
  isCatalogPageEvent,
} from "@/features/catalog/mediators/catalogPageMediator";
import {
  createCatalogContext,
  type SortType,
  useCatalogStore,
} from "@/features/catalog/stores/catalogStore";
import { catalogContextBoardSlug } from "@/features/catalog/utils/catalogContext";
import {
  DesktopHistorySidebar,
  type DesktopHistorySidebarHandle,
} from "@/features/history/components/DesktopHistorySidebar";
import { DesktopSettingsModal } from "@/features/settings/components/desktop/DesktopSettingsModal";
import { useSettingsStore } from "@/features/settings/hooks";
import { DesktopThreadCreatePanel } from "@/features/thread/components/desktop/DesktopThreadCreatePanel";
import { ThreadCreateModal } from "@/features/thread/components/modals/ThreadCreateModal";
import { useOpenPanelOnFileDrag } from "@/features/thread/hooks/useOpenPanelOnFileDrag";
import { useThreadCreateModalStore } from "@/features/thread/stores/threadCreateModalStore";
import { useIsDesktop } from "@/shared/hooks";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useManualRefresh } from "@/shared/hooks/useManualRefresh";
import { BmgBanner } from "@/shared/ui/ad";
import { DmmWidget } from "@/shared/ui/ad/DmmWidget";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { PullRefresh, RefreshStatus } from "@/shared/ui/feedback";
import { TextLink } from "@/shared/ui/navigation/TextLink";

export const CatalogPage: React.FunctionComponent = () => {
  useDocumentTitle(BOARD_DISPLAY_NAME);
  const updateLastCatalogIds = useCatalogStore(
    (state) => state.updateLastCatalogIds,
  );
  const currentSort = useCatalogStore((state) => state.currentSort);
  const selectSortMethod = useCatalogStore((state) => state.selectSortMethod);
  const page = useCatalogStore((state) => state.page);
  const setPage = useCatalogStore((state) => state.setPage);
  const autoReload = useCatalogStore((state) => state.autoReload);
  const setAutoReload = useCatalogStore((state) => state.setAutoReload);
  const isOpen = useThreadCreateModalStore((s) => s.isOpen);
  const open = useThreadCreateModalStore((s) => s.open);
  const close = useThreadCreateModalStore((s) => s.close);
  const desktopThreadCreateButtonRef = useRef<HTMLButtonElement>(null);
  const desktopHistorySidebarRef = useRef<DesktopHistorySidebarHandle>(null);
  const isDesktop = useIsDesktop();
  const showDmmWidget = useSettingsStore((state) => !state.privacyMode);
  const threadsQuery = useThreads(undefined, { enabled: isDesktop });
  const infiniteThreadsQuery = useInfiniteThreads({ enabled: !isDesktop });
  const activeQuery = isDesktop ? threadsQuery : infiniteThreadsQuery;
  const { data, isFetching } = activeQuery;
  const refetch = useCallback(
    () => (isDesktop ? threadsQuery.refetch() : infiniteThreadsQuery.refetch()),
    [infiniteThreadsQuery.refetch, isDesktop, threadsQuery.refetch],
  );
  const latestThreadsRef = useRef(data?.threads);
  useEffect(() => {
    latestThreadsRef.current = data?.threads;
  }, [data?.threads]);
  const wheelReloadEnabled = useSettingsStore(
    (state) => state.wheelReloadEnabled,
  );
  const navigate = useNavigate();

  // Issue #208 はPCの手動更新だけを統一するため、SPのPullRefreshは
  // 従来どおり自動取得中の再実行を抑止する既存経路を維持する。
  const onRefresh = useCallback(async () => {
    if (isFetching) return;
    await refetch();
  }, [isFetching, refetch]);
  const pullRefreshOperation = useRef<Promise<void>>(Promise.resolve());
  const { isManualRefreshing, refreshManually } = useManualRefresh(
    refetch,
    isFetching,
  );

  const closeDesktopThreadCreatePanel = useCallback((): void => {
    close();
    window.requestAnimationFrame(() => {
      desktopThreadCreateButtonRef.current?.focus();
    });
  }, [close]);
  const effectRunner = useRef<(effect: CatalogPageEffect) => void>(
    (): void => undefined,
  );
  effectRunner.current = (effect: CatalogPageEffect): void => {
    switch (effect.type) {
      case "catalog/sort-selected":
        selectSortMethod(effect.sort);
        return;
      case "catalog/page-selected":
        setPage(effect.page);
        return;
      case "catalog/auto-reload-changed":
        setAutoReload(effect.enabled);
        return;
      case "catalog/refresh-requested":
        if (effect.manual) {
          void refreshManually();
        } else {
          pullRefreshOperation.current = onRefresh();
        }
        return;
      case "catalog/retry-requested":
        void refetch();
        return;
      case "catalog/load-more-requested":
        void infiniteThreadsQuery.fetchNextPage();
        return;
      case "catalog/sidebar-toggle-requested":
        desktopHistorySidebarRef.current?.toggle();
        return;
      case "catalog/scroll-requested":
        window.scrollTo({
          top:
            effect.edge === "top" ? 0 : document.documentElement.scrollHeight,
        });
        return;
      case "catalog/list-view-requested":
        void navigate({ to: "/board" });
        return;
      case "catalog/thread-create-open-requested":
        open();
        return;
      case "catalog/thread-create-close-requested":
        if (isDesktop) closeDesktopThreadCreatePanel();
        else close();
    }
  };
  const [mediator] = useState(() =>
    createCatalogPageMediator(
      {
        isDesktop,
        isThreadCreateOpen: isOpen,
        isFetching,
        hasNextPage: infiniteThreadsQuery.hasNextPage,
      },
      (effect) => effectRunner.current(effect),
    ),
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (!isCatalogEvent(event) || !isCatalogPageEvent(event)) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const pageState = useMediatorSnapshot(mediator);
  const notify: CatalogEventNotifier = (
    event: CatalogEvent,
  ): ReturnType<CatalogEventNotifier> => node.dispatch(event).status;
  const openThreadCreateOnFileDrag = useOpenPanelOnFileDrag(isOpen, () => {
    notify({ type: "catalog/thread-create-open-requested" });
  });

  useEffect(() => {
    mediator.dispatch({
      type: "catalog/page-model-synchronized",
      isDesktop,
      isThreadCreateOpen: isOpen,
      isFetching,
      hasNextPage: infiniteThreadsQuery.hasNextPage,
    });
  }, [
    infiniteThreadsQuery.hasNextPage,
    isDesktop,
    isFetching,
    isOpen,
    mediator,
  ]);

  // contextが切り替わる直前に、そのcontextで最後に取得した一覧を保存する。
  useEffect(() => {
    const context = createCatalogContext(
      catalogContextBoardSlug(),
      isDesktop ? page : 1,
      currentSort,
    );
    return (): void => {
      const threads = latestThreadsRef.current;
      if (threads) {
        updateLastCatalogIds(
          context,
          threads.map((thread) => thread.id),
        );
      }
    };
  }, [currentSort, isDesktop, page, updateLastCatalogIds]);

  // useThreadCreateModalStore はグローバルな zustand なので、ページ離脱時に
  // close() しないと再訪時に modal が開きっぱなしになる
  useEffect(() => {
    return (): void => {
      close();
    };
  }, [close]);

  return (
    <ComponentScope node={node}>
      <CatalogPageView
        isDesktop={isDesktop}
        showOriginalBoardLink={
          !IS_MIX_CATALOG && BOARD_CONFIG.boardKey === "touhou"
        }
        data={data}
        threadsQuery={threadsQuery}
        infiniteThreadsQuery={infiniteThreadsQuery}
        currentSort={currentSort}
        autoReload={autoReload}
        isFetching={isFetching}
        isManualRefreshing={isManualRefreshing}
        wheelReloadEnabled={wheelReloadEnabled}
        page={page}
        showDmmWidget={showDmmWidget}
        isThreadCreateOpen={isOpen}
        isSettingsOpen={pageState.isSettingsOpen}
        isSortOpen={pageState.isSortOpen}
        isTagFilterOpen={pageState.isTagFilterOpen}
        desktopThreadCreateButtonRef={desktopThreadCreateButtonRef}
        desktopHistorySidebarRef={desktopHistorySidebarRef}
        openThreadCreateOnFileDrag={openThreadCreateOnFileDrag}
        onEvent={(event): void => {
          node.dispatch(event);
        }}
        onDesktopRefresh={refreshManually}
        onPullRefresh={(): Promise<void> => {
          notify({ type: "catalog/refresh-requested", manual: false });
          return pullRefreshOperation.current;
        }}
      />
    </ComponentScope>
  );
};

interface CatalogPageViewProps {
  readonly isDesktop: boolean;
  readonly showOriginalBoardLink: boolean;
  readonly data: ReturnType<typeof useThreads>["data"];
  readonly threadsQuery: ReturnType<typeof useThreads>;
  readonly infiniteThreadsQuery: ReturnType<typeof useInfiniteThreads>;
  readonly currentSort: SortType;
  readonly autoReload: boolean;
  readonly isFetching: boolean;
  readonly isManualRefreshing: boolean;
  readonly wheelReloadEnabled: boolean;
  readonly page: number;
  readonly showDmmWidget: boolean;
  readonly isThreadCreateOpen: boolean;
  readonly isSettingsOpen: boolean;
  readonly isSortOpen: boolean;
  readonly isTagFilterOpen: boolean;
  readonly desktopThreadCreateButtonRef: React.RefObject<HTMLButtonElement | null>;
  readonly desktopHistorySidebarRef: React.RefObject<DesktopHistorySidebarHandle | null>;
  readonly openThreadCreateOnFileDrag: React.DragEventHandler;
  readonly onEvent: (event: CatalogEvent) => void;
  readonly onDesktopRefresh: () => void;
  readonly onPullRefresh: () => Promise<void>;
}

export function CatalogPageView({
  isDesktop,
  showOriginalBoardLink,
  data,
  threadsQuery,
  infiniteThreadsQuery,
  currentSort,
  autoReload,
  isFetching,
  isManualRefreshing,
  wheelReloadEnabled,
  page,
  showDmmWidget,
  isThreadCreateOpen: isOpen,
  isSettingsOpen,
  isSortOpen,
  isTagFilterOpen,
  desktopThreadCreateButtonRef,
  desktopHistorySidebarRef,
  openThreadCreateOnFileDrag,
  onEvent,
  onDesktopRefresh,
  onPullRefresh,
}: CatalogPageViewProps): React.ReactElement {
  const notify = onEvent;
  const originalBoardLink = showOriginalBoardLink ? (
    <p className="py-2 text-center text-base">
      <span className="text-destructive">本家様は</span>
      <TextLink href="https://dec.2chan.net/55/futaba.htm">こちら</TextLink>
    </p>
  ) : null;

  if (isDesktop) {
    return (
      <>
        <section
          aria-labelledby="catalog-page-title"
          className="desktop-catalog-page"
        >
          <DesktopCatalogToolbar
            tagFilterThreads={data?.threads ?? []}
            currentSort={currentSort}
            autoReload={autoReload}
            isFetching={isFetching}
          />
          <RefreshStatus isRefreshing={isManualRefreshing} />
          <h1 id="catalog-page-title" className="desktop-mode-title">
            カタログ
          </h1>
          <DesktopAnnouncementContent>
            <section
              className="desktop-catalog-banner"
              aria-label="お知らせと広告"
            >
              <div className="desktop-catalog-announcement-slot">
                <AnnounceBanner />
              </div>
              <BmgBanner reserveEmptySpace priority />
            </section>
            {originalBoardLink}
            <CatalogGrid
              query={threadsQuery}
              onDesktopRefresh={onDesktopRefresh}
              wheelReloadEnabled={wheelReloadEnabled}
            />
            {data && (
              <CatalogPagination
                page={page}
                hasNextPage={data.pagination.hasNextPage}
                isFetching={isFetching}
                onChange={(nextPage: number): void => {
                  notify({ type: "catalog/page-selected", page: nextPage });
                }}
              />
            )}
            {data && showDmmWidget && <DmmWidget />}
          </DesktopAnnouncementContent>
          <DesktopHistorySidebar
            ref={desktopHistorySidebarRef}
            showClosedHandle={false}
          />
          <DesktopThreadCreatePanel
            isOpen={isOpen}
            onClose={(): void => {
              notify({ type: "catalog/thread-create-close-requested" });
            }}
          />
          <DesktopSettingsModal
            isOpen={isSettingsOpen}
            onClose={(): void => {
              notify({ type: "catalog/settings-close-requested" });
            }}
          />
          <button
            ref={desktopThreadCreateButtonRef}
            className="desktop-catalog-fab"
            type="button"
            aria-expanded={isOpen}
            onClick={(): void => {
              notify({ type: "catalog/thread-create-toggle-requested" });
            }}
            onDragEnter={openThreadCreateOnFileDrag}
          >
            {isOpen ? (
              <FiX aria-hidden="true" />
            ) : (
              <FiPlus aria-hidden="true" />
            )}
            {isOpen ? "閉じる" : "新規スレ"}
          </button>
        </section>
      </>
    );
  }

  return (
    <>
      <section aria-labelledby="catalog-page-title" className="pb-20">
        <h1 id="catalog-page-title" className="sr-only">
          カタログ
        </h1>
        <AnnounceBanner />
        <BmgBanner reserveEmptySpace priority />
        {originalBoardLink}
        <PullRefresh onRefresh={onPullRefresh}>
          <CatalogGrid
            query={infiniteThreadsQuery}
            onDesktopRefresh={(): void => undefined}
            wheelReloadEnabled={wheelReloadEnabled}
          />
          {data && (
            <CatalogLoadMore
              errorMessage={infiniteThreadsQuery.nextPageError}
              hasNextPage={infiniteThreadsQuery.hasNextPage}
              isFetching={isFetching}
              onLoadMore={(): void => {
                notify({ type: "catalog/load-more-requested" });
              }}
            />
          )}
        </PullRefresh>
      </section>
      <MobileCatalogFooter
        isRefreshing={isFetching}
        onOpenSort={(): void => {
          notify({ type: "catalog/sort-modal-open-requested" });
        }}
        onOpenTagFilter={(): void => {
          notify({ type: "catalog/tag-modal-open-requested" });
        }}
        onRefresh={(): void => {
          notify({ type: "catalog/refresh-requested", manual: false });
        }}
        primaryAction={{
          icon: FiPlus,
          label: "新規スレッド",
          onClick: (): void => {
            notify({ type: "catalog/thread-create-open-requested" });
          },
        }}
      />
      {isSortOpen && (
        <CatalogSortModal
          isOpen
          currentSort={currentSort}
          selectSortMethod={(sort: SortType): void => {
            notify({ type: "catalog/sort-selected", sort });
          }}
          onShowCatalog={(): void => undefined}
          onShowList={(): void => {
            notify({ type: "catalog/list-view-requested" });
          }}
          onClose={(): void => {
            notify({ type: "catalog/sort-modal-close-requested" });
          }}
        />
      )}
      <TagFilterModal
        isOpen={isTagFilterOpen}
        threads={data?.threads ?? []}
        onClose={(): void => {
          notify({ type: "catalog/tag-modal-close-requested" });
        }}
      />
      <ThreadCreateModal
        isOpen={isOpen}
        onClose={(): void => {
          notify({ type: "catalog/thread-create-close-requested" });
        }}
      />
    </>
  );
}
